import { IconButton, Stack, Typography } from "@mui/material";
import { SearchIcon } from "../../../images/SearchIcon";
import { useEffect, useState } from "react";
import { useDebounceValue } from "../../../../hooks/useDebounceValue";
import { ChevronLeftIcon, PlusIcon } from "../../../images";
import { CustomButton } from "../../CustomButton";
import { CustomInput } from "../../CustomInput";
import { NAME_REGEX } from "../../../../utils/regex";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";

type Props = {
  setSearchValue: (searchValue: string) => void;
  addTitleHandler: (title: string, successHandler: () => void) => void;
  searchElIndex: number;
  searchTotalCount: number;
  setSearchElIndex: (index: number) => void;
  isLimit: boolean;
};

export const JobTitlesListHeader = ({
  setSearchValue,
  addTitleHandler,
  searchElIndex,
  searchTotalCount,
  setSearchElIndex,
  isLimit,
}: Props) => {
  const [value, setValue] = useState<string>("");
  const [title, setTitle] = useState<string>("");
  const debounceValue = useDebounceValue(value);

  useEffect(() => {
    setSearchValue(debounceValue);
  }, [debounceValue]);

  const prevHandler = () => {
    if (searchElIndex === 0) return;
    setSearchElIndex(searchElIndex - 1);
  };

  const nextHandler = () => {
    if (searchElIndex === searchTotalCount - 1) return;
    setSearchElIndex(searchElIndex + 1);
  };

  const createTitleHandler = () => {
    const trimmedTitle = title.trim();
    if (
      !trimmedTitle ||
      !NAME_REGEX.test(trimmedTitle) ||
      trimmedTitle.length < 3 ||
      trimmedTitle.length > 100
    ) {
      toast.error(
        <CustomToast
          type="error"
          title="Invalid Job Title"
          text="Must contain only letters, numbers, and basic punctuation.
             Please enter a valid job title using 3 to 100 characters."
        />,
      );
      return;
    }

    addTitleHandler(trimmedTitle, () => setTitle(""));
  };

  return (
    <Stack gap="16px">
      <Stack
        flexDirection="row"
        gap="8px"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <CustomInput
          value={title}
          placeholder="Enter job title you want to add"
          onChange={setTitle}
          minHeight="42px"
        />
        <Stack
          flexDirection="row"
          gap="4px"
          alignItems="center"
          minWidth="170px"
          width="170px"
        >
          <CustomButton
            onClick={createTitleHandler}
            variant="outlined"
            customColor="#FFFFFF"
            textColor="#1D3E70"
            type="button"
            fullWidth={true}
            startIcon={<PlusIcon opacity={isLimit ? "0.5" : "1"} />}
            disabled={isLimit}
            sx={{
              height: "42px",
              borderColor: "#1D3E70",
              flexGrow: 1,
              textWrap: "nowrap",
            }}
          >
            {isLimit ? "Limit Reached" : "Add Job Title"}
          </CustomButton>
        </Stack>
      </Stack>
      <Stack flexDirection="row" gap="8px" alignItems="center">
        <CustomInput
          value={value}
          placeholder="Search"
          prefixIcon={<SearchIcon />}
          onChange={setValue}
          clearButton
          minHeight="42px"
        />
        {value && (
          <Stack
            flexDirection="row"
            gap="4px"
            alignItems="center"
            minWidth="156px"
            width="156px"
          >
            <IconButton onClick={prevHandler} disabled={searchElIndex === 0}>
              <ChevronLeftIcon
                rotate={"90"}
                opacity={searchElIndex === 0 ? "0.2" : "1"}
              />
            </IconButton>
            <Typography
              variant="subtitle1"
              color="#1F293299"
              component="span"
              noWrap
            >
              {searchElIndex + 1} from {searchTotalCount}
            </Typography>
            <IconButton
              onClick={nextHandler}
              disabled={searchElIndex === searchTotalCount - 1}
            >
              <ChevronLeftIcon
                rotate={"-90"}
                opacity={searchElIndex === searchTotalCount - 1 ? "0.2" : "1"}
              />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};
